import React, { useState } from 'react';
import { Smile, Copy, Trash2 } from 'lucide-react';

const TextToEmojiTool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const emojiMap = {
    'happy': '😊', 'sad': '😢', 'love': '❤️', 'heart': '❤️', 'fire': '🔥',
    'hot': '🔥', 'cool': '😎', 'laugh': '😂', 'lol': '😂', 'cry': '😭',
    'angry': '😠', 'mad': '😠', 'think': '🤔', 'hmm': '🤔', 'star': '⭐',
    'sun': '☀️', 'moon': '🌙', 'rain': '🌧️', 'snow': '❄️', 'cloud': '☁️',
    'coffee': '☕', 'tea': '🍵', 'beer': '🍺', 'wine': '🍷', 'food': '🍔',
    'pizza': '🍕', 'cake': '🎂', 'apple': '🍎', 'banana': '🍌', 'dog': '🐕',
    'cat': '🐱', 'bird': '🐦', 'fish': '🐟', 'tree': '🌳', 'flower': '🌸',
    'car': '🚗', 'plane': '✈️', 'rocket': '🚀', 'house': '🏠', 'phone': '📱',
    'computer': '💻', 'book': '📚', 'money': '💰', 'time': '⏰', 'clock': '🕐',
    'music': '🎵', 'movie': '🎬', 'game': '🎮', 'sport': '⚽', 'ball': '🏀',
    'run': '🏃', 'walk': '🚶', 'dance': '💃', 'sleep': '😴', 'work': '💼',
    'idea': '💡', 'yes': '✅', 'no': '❌', 'ok': '👍', 'good': '👍',
    'bad': '👎', 'hello': '👋', 'bye': '👋', 'thank': '🙏', 'please': '🙏',
    'party': '🎉', 'gift': '🎁', 'birthday': '🎂', 'christmas': '🎄', 'halloween': '🎃',
    'warning': '⚠️', 'stop': '🛑', 'check': '✔️', 'question': '❓', 'exclaim': '❗',
    'world': '🌍', 'earth': '🌍', 'water': '💧', 'wave': '🌊', 'mountain': '⛰️'
  };

  const convertToEmoji = (text) => {
    if (!text.trim()) {
      setOutput('');
      return;
    }

    let result = text.toLowerCase();
    
    // Replace words with emojis
    Object.entries(emojiMap).forEach(([word, emoji]) => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      result = result.replace(regex, emoji);
    });

    // Convert letters to regional indicator emojis (optional mode)
    setOutput(result);
  };

  const convertLettersToEmoji = () => {
    if (!input.trim()) return;
    
    const letterEmojis = {
      'a': '🅰️', 'b': '🅱️', 'o': '🅾️', 'p': '🅿️',
    };
    
    let result = '';
    for (const char of input.toLowerCase()) {
      if (letterEmojis[char]) {
        result += letterEmojis[char];
      } else if (/[a-z]/.test(char)) {
        // Convert to regional indicator
        result += String.fromCodePoint(0x1F1E6 + char.charCodeAt(0) - 97) + ' ';
      } else {
        result += char;
      }
    }
    setOutput(result);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    convertToEmoji(e.target.value);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
        <p className="text-sm text-blue-300">
          Type words like "happy", "love", "fire", "coffee", "dog", "star" and they'll be converted to emojis!
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Input Text
        </label>
        <textarea
          value={input}
          onChange={handleInputChange}
          placeholder="Type something... try words like 'I love coffee and my dog'"
          className="w-full h-32 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => convertToEmoji(input)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Smile size={16} />
          Convert Words
        </button>
        <button
          onClick={convertLettersToEmoji}
          disabled={!input.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-white rounded-lg transition-colors"
        >
          Convert Letters
        </button>
      </div>

      {output && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Emoji Output
          </label>
          <div className="w-full min-h-32 bg-gray-800 border border-gray-700 rounded-lg p-4 text-2xl">
            {output}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Supported Words</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(emojiMap).slice(0, 30).map(([word, emoji]) => (
            <span key={word} className="px-2 py-1 bg-gray-700 rounded text-sm">
              {word} {emoji}
            </span>
          ))}
          <span className="px-2 py-1 bg-gray-600 rounded text-sm text-gray-400">
            +{Object.keys(emojiMap).length - 30} more...
          </span>
        </div>
      </div>
    </div>
  );
};

export default TextToEmojiTool;
